import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Shield,
  Plus,
  Edit,
  Trash2,
  Search,
  X,
  Users,
  Filter,
} from "../components/icons";
import { apiClient } from "../lib/api";
import { normalizePlayers, type PlayerApiResponse } from "../lib/players";
import {
  buildTeamPayload,
  normalizeTeams,
  type TeamApiResponse,
} from "../lib/teams";
import type {
  Team,
  TeamPlayer,
  PlayerData,
  PlayerPosition,
  PaginatedResponse,
  TechnicalStaffMember,
} from "../types";
import { getCountriesSorted } from "../lib/countries";
import { useDuplicateCheck } from "../hooks/useDuplicateCheck";
import { useChangeDetection } from "../hooks/useChangeDetection";
import { useLoading } from "../hooks/useLoading";
import DuplicateWarning from "../components/DuplicateWarning";
import ChangeConfirmation from "../components/ChangeConfirmation";
import SimilarRecordsViewer from "../components/SimilarRecordsViewer";
import { useAuthStore } from "../store/authStore";
import Pagination from "../components/Pagination";
import LoadingSpinner from "../components/LoadingSpinner";
import AutocompleteSearch from "../components/AutocompleteSearch";
import { useFormValidation } from "../hooks/useFormValidation";
import { teamSchema } from "../lib/validationSchemas";
import {
  applyBackendValidationErrors,
  resolveKnownFieldError,
} from "../lib/backendErrorUtils";

// Technical staff roles (stored in English in DB, translated in UI)
// Note: Manager and Technical Director are not included as they are leadership positions
const TECHNICAL_STAFF_ROLES = [
  "Assistant Coach",
  "Shooting Coach",
  "Fitness Coach",
  "Team Doctor",
  "Physiotherapist",
  "Nutritionist",
  "Sports Psychologist",
  "Video Analyst",
  "Scout",
  "Equipment Manager",
  "Other",
] as const;

const ADMIN_LOCALES = ["en", "es"] as const;
type AdminLocale = (typeof ADMIN_LOCALES)[number];

const ROSTER_POSITION_OPTIONS: PlayerPosition[] = [
  "PG",
  "SG",
  "SF",
  "PF",
  "C",
];

const withTeamFormDefaults = (data?: Partial<Team>): Partial<Team> => {
  const merged = { ...(data ?? {}) };
  const localizedNames: Record<string, string> = {
    ...(merged.i18n_names ?? {}),
  };

  ADMIN_LOCALES.forEach((locale) => {
    localizedNames[locale] = localizedNames[locale] ?? "";
  });

  return {
    ...merged,
    name: merged.name ?? "",
    short_name: merged.short_name ?? "",
    country_name: merged.country_name ?? "",
    gender: merged.gender ?? "male",
    manager: merged.manager ?? {
      name: "",
      country_name: "",
      start_date: null,
    },
    managers: merged.managers ?? [],
    technical_staff: merged.technical_staff ?? [],
    i18n_names: localizedNames,
  };
};

export default function TeamsManager() {
  const { t, i18n } = useTranslation(["admin", "common"]);
  const user = useAuthStore((state) => state.user);
  const [teams, setTeams] = useState<Team[]>([]);
  const [players, setPlayers] = useState<PlayerData[]>([]);
  const { loading, withLoading } = useLoading(true);
  const [hasInitialLoadCompleted, setHasInitialLoadCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [genderFilter, setGenderFilter] = useState<"male" | "female" | "">("");
  const [showForm, setShowForm] = useState(false);
  const [showRosterModal, setShowRosterModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Team | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teamRoster, setTeamRoster] = useState<TeamPlayer[]>([]);
  const [availablePlayers, setAvailablePlayers] = useState<PlayerData[]>([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [showChangeWarning, setShowChangeWarning] = useState(false);
  const [showSimilarRecords, setShowSimilarRecords] = useState(false);
  const [similarRecords, setSimilarRecords] = useState<any[] | null>(null);
  const [originalFormData, setOriginalFormData] =
    useState<Partial<Team> | null>(null);
  const currentLang = i18n.language as "en" | "es";
  const { checkDuplicates, duplicates, clearDuplicates } = useDuplicateCheck();
  const { detectChanges, changeResult, duplicateResult, clearChangeResult } =
    useChangeDetection();

  // Form validation
  const {
    validateForm,
    validateAndSetFieldError,
    clearErrors,
    getFieldError,
    setFieldError,
  } = useFormValidation<Partial<Team>>(teamSchema, t);

  const formatDeleteGuardError = (detail: unknown): string | null => {
    if (typeof detail !== "string") return null;
    const match = detail.match(/Cannot delete team with (\d+) match(?:es)?/i);
    if (!match) return null;
    const count = Number(match[1]);
    return t("deleteGuards.teamMatches", { count });
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Roster pagination
  const [rosterPage, setRosterPage] = useState(1);
  const [rosterPageSize, setRosterPageSize] = useState(10);
  const [rosterTotalItems, setRosterTotalItems] = useState(0);
  const [isRosterModalLoading, setIsRosterModalLoading] = useState(false);
  const [isRosterPaginationLoading, setIsRosterPaginationLoading] =
    useState(false);
  const [isTeamsPaginationLoading, setIsTeamsPaginationLoading] =
    useState(false);

  const PLAYER_FETCH_PAGE_SIZE = 100;

  const [formData, setFormData] = useState<Partial<Team>>(() =>
    withTeamFormDefaults(),
  );

  const getManagerDraft = () =>
    formData.manager ?? {
      name: "",
      country_name: "",
      start_date: null,
    };

  const [rosterFormData, setRosterFormData] = useState({
    player_id: "",
    jersey_number: 1,
    position: "PG" as PlayerPosition,
    is_active: true,
  });
  const [rosterFormError, setRosterFormError] = useState<string | null>(null);
  const [rosterFieldErrors, setRosterFieldErrors] = useState<
    Record<string, string>
  >({});
  const [rosterPlayerSearch, setRosterPlayerSearch] = useState("");
  const [isPlayerPickerOpen, setIsPlayerPickerOpen] = useState(false);
  const [isAddingPlayerToRoster, setIsAddingPlayerToRoster] = useState(false);
  const [isRosterPositionPickerOpen, setIsRosterPositionPickerOpen] =
    useState(false);
  const [rosterPositionFilter, setRosterPositionFilter] = useState<
    PlayerPosition | "ALL"
  >("ALL");
  const [isRosterFilterOpen, setIsRosterFilterOpen] = useState(false);

  const clearRosterFieldErrors = () => setRosterFieldErrors({});
  const setRosterFieldError = (field: string, message: string) => {
    setRosterFieldErrors((prev) => ({ ...prev, [field]: message }));
  };
  const clearRosterFieldError = (field: string) => {
    setRosterFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };
  const handleRosterStringError = (detail?: string): boolean => {
    if (!detail) {
      return false;
    }
    console.warn("[TeamsManager] roster string error", detail);
    const normalized = detail.toLowerCase();
    if (normalized.includes("jersey number")) {
      setRosterFieldError("jersey_number", detail);
      setRosterFormError(t("validation.fixErrors"));
      return true;
    }
    if (normalized.includes("already in team")) {
      setRosterFieldError("player_id", detail);
      setRosterFormError(t("validation.fixErrors"));
      return true;
    }
    return false;
  };

  useEffect(() => {
    fetchTeams();
  }, [currentPage, pageSize, genderFilter, searchTerm]);

  useEffect(() => {
    fetchAllPlayers();
  }, []);

  const fetchTeams = async () => {
    await withLoading(async () => {
      try {
        setError(null);
        const params: any = {
          page: currentPage,
          page_size: pageSize,
        };

        if (genderFilter) {
          params.gender = genderFilter;
        }

        if (searchTerm) {
          params.search = searchTerm;
        }

        const response = await apiClient.get<
          PaginatedResponse<TeamApiResponse>
        >("/teams/", { params });
        setTeams(normalizeTeams(response.items));
        setTotalItems(response.total);
        setTotalPages(response.total_pages);
      } catch (err: any) {
        setError(err.response?.data?.detail || t("errorFetchingData"));
      } finally {
        setHasInitialLoadCompleted(true);
        setIsTeamsPaginationLoading(false);
      }
    });
  };

  const fetchAllPlayers = async (): Promise<PlayerData[]> => {
    try {
      const allPlayers: PlayerApiResponse[] = [];
      let page = 1;
      let totalPages = 1;

      do {
        const response = await apiClient.get<
          PaginatedResponse<PlayerApiResponse>
        >("/players/", {
          params: { page, page_size: PLAYER_FETCH_PAGE_SIZE },
        });

        allPlayers.push(...response.items);
        totalPages = Math.max(1, response.total_pages || 1);
        page += 1;
      } while (page <= totalPages);

      const normalizedPlayers = normalizePlayers(allPlayers);
      setPlayers(normalizedPlayers);
      return normalizedPlayers;
    } catch (err: any) {
      console.error("Error fetching players:", err);
      return [];
    }
  };

  const fetchTeamRoster = async (
    teamId: string,
    mode: "modal" | "pagination" | "refresh" = "refresh",
    _pageOverride?: number,
    _pageSizeOverride?: number,
    playerPool?: PlayerData[],
  ) => {
    if (mode === "modal") {
      setIsRosterModalLoading(true);
    }
    if (mode === "pagination") {
      setIsRosterPaginationLoading(true);
    }

    try {
      // Fetch ALL roster items so we sort globally, not per-page
      const allItems: TeamPlayer[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const resp = await apiClient.get<PaginatedResponse<TeamPlayer>>(
          `/teams/${teamId}/players`,
          { params: { page, page_size: 100 } },
        );
        allItems.push(...resp.items);
        totalPages = Math.max(1, resp.total_pages || 1);
        page += 1;
      } while (page <= totalPages);

      setTeamRoster(allItems);
      setRosterTotalItems(allItems.length);

      const rosterPlayerIds = allItems.map((tp) => tp.player_id);
      const sourcePlayers = playerPool ?? players;
      const filtered = sourcePlayers.filter(
        (p) => !rosterPlayerIds.includes(p.player_id),
      );
      setAvailablePlayers(filtered);
    } catch (err: any) {
      setError(err.response?.data?.detail || t("errorFetchingData"));
    } finally {
      if (mode === "modal") {
        setIsRosterModalLoading(false);
      }
      if (mode === "pagination") {
        setIsRosterPaginationLoading(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm(formData)) {
      setError(t("validation.fixErrors"));
      return;
    }

    const backendPayload = buildTeamPayload(formData);

    // EDIT MODE: Always show changes for confirmation
    if (editingItem && editingItem.team_id) {
      // If we haven't shown the change warning yet, detect changes with duplicate check
      if (!showChangeWarning) {
        // Always clear previous result and detect fresh with combined duplicate check
        clearChangeResult();
        clearDuplicates();

        // Detect changes AND check duplicates in single API call
        const result = await detectChanges(
          "teams",
          editingItem.team_id,
          backendPayload,
          true,
        );

        // Always show change confirmation, even for small changes
        if (result && result.changes.length > 0) {
          setShowChangeWarning(true);
          return;
        } else if (result && result.changes.length === 0) {
          // No changes detected, just save
          setError(t("validation.noChangesDetected"));
          return;
        }
      }

      // After change confirmation, check if duplicates were already detected
      if (showChangeWarning && changeResult) {
        // Duplicates were already checked in combined call, just show them if found
        if (
          duplicateResult &&
          duplicateResult.has_duplicates &&
          !showSimilarRecords
        ) {
          setShowChangeWarning(false);
          setSimilarRecords(duplicateResult.duplicates ?? []);
          setShowSimilarRecords(true);
          return;
        }
      }
    }

    // CREATE MODE: Check for duplicates
    if (!editingItem) {
      const result = await checkDuplicates("teams", backendPayload);

      // Check for 100% duplicates (should be rejected automatically)
      const exactDuplicate = result.duplicates?.find(
        (d) => d.similarity_score === 1.0 || d.match_score === 100,
      );

      if (exactDuplicate) {
        setError(t("validation.exactDuplicateExists"));
        return;
      }

      // Check for similar duplicates (80%+)
      if (result.has_duplicates && !showDuplicateWarning) {
        setShowDuplicateWarning(true);
        return;
      }
    }

    await saveTeam(backendPayload);
  };

  const saveTeam = async (payloadOverride?: Record<string, unknown>) => {
    try {
      const payload = payloadOverride ?? buildTeamPayload(formData);

      if (editingItem) {
        await apiClient.put(`/teams/${editingItem.team_id}`, payload);
      } else {
        await apiClient.post("/teams/", payload);
      }
      await fetchTeams();
      handleCloseForm();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const handled = applyBackendValidationErrors(detail, {
        setFieldError,
        clearErrors,
        translate: t,
      });
      if (handled) {
        setError(t("validation.fixErrors"));
        return;
      }

      const knownFieldError = resolveKnownFieldError(detail);
      if (knownFieldError) {
        setFieldError(knownFieldError.field, t(knownFieldError.translationKey));
        setError(t("validation.fixErrors"));
        return;
      }
      if (typeof detail === "string") {
        setError(detail);
      } else {
        setError(t("errorSavingData"));
      }
    }
  };

  const handleContinueWithDuplicates = async () => {
    setShowDuplicateWarning(false);
    clearDuplicates();
    await saveTeam(buildTeamPayload(formData));
  };

  const handleCancelDuplicates = () => {
    setShowDuplicateWarning(false);
    clearDuplicates();
  };

  const handleConfirmChanges = async () => {
    // User confirmed the changes, proceed to next step (duplicate check)
    setShowChangeWarning(false);
    // Don't clear changeResult here, we need it for the duplicate check

    // Continue with the duplicate check and save flow
    await handleSubmit(new Event("submit") as any);
  };

  const handleCancelChanges = () => {
    // User canceled, reset everything
    setShowChangeWarning(false);
    clearChangeResult();
    setError(null);

    // Revert to original data
    if (originalFormData) {
      setFormData(originalFormData);
    }
  };

  const handleCloseSimilarRecords = () => {
    setShowSimilarRecords(false);
    setSimilarRecords(null);
    clearChangeResult();
  };

  const handleOpenSimilarRecord = (record: any) => {
    setShowSimilarRecords(false);
    setSimilarRecords(null);
    clearChangeResult();
    handleEdit(record);
  };

  const handleLocalizedNameChange = (locale: AdminLocale, value: string) => {
    setFormData((prev) => ({
      ...prev,
      i18n_names: {
        ...(prev.i18n_names ?? {}),
        [locale]: value,
      },
    }));
    validateAndSetFieldError(`i18n_names.${locale}`, value);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await apiClient.delete(`/teams/${id}`);
      await fetchTeams();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const guardMessage = formatDeleteGuardError(detail);
      if (guardMessage) {
        setError(guardMessage);
        return;
      }
      setError(typeof detail === "string" ? detail : t("errorDeletingData"));
    }
  };

  const handleEdit = (item: Team) => {
    setEditingItem(item);
    const formDataToSet = withTeamFormDefaults(item);
    setFormData(formDataToSet);
    setOriginalFormData(formDataToSet); // Store original data for revert
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingItem(null);
    setShowDuplicateWarning(false);
    setShowChangeWarning(false);
    setShowSimilarRecords(false);
    setOriginalFormData(null);
    clearDuplicates();
    clearChangeResult();
    clearErrors();
    setError(null);
    setFormData(withTeamFormDefaults());
  };

  // Technical staff handlers
  const handleAddStaff = () => {
    const newStaff: TechnicalStaffMember = {
      name: "",
      role: "",
      country_name: "",
      start_date: null,
    };
    setFormData({
      ...formData,
      technical_staff: [...(formData.technical_staff || []), newStaff],
    });
  };

  const handleRemoveStaff = (index: number) => {
    const updatedStaff = [...(formData.technical_staff || [])];
    updatedStaff.splice(index, 1);
    setFormData({ ...formData, technical_staff: updatedStaff });
  };

  const handleUpdateStaff = (
    index: number,
    field: keyof TechnicalStaffMember,
    value: any,
  ) => {
    const updatedStaff = [...(formData.technical_staff || [])];
    updatedStaff[index] = { ...updatedStaff[index], [field]: value };
    setFormData({ ...formData, technical_staff: updatedStaff });
  };

  // Fetch team suggestions for autocomplete
  const fetchTeamSuggestions = async (
    query: string,
  ): Promise<TeamApiResponse[]> => {
    try {
      const response = await apiClient.get<TeamApiResponse[]>(
        "/teams/search/suggestions",
        {
          params: { q: query },
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching team suggestions:", error);
      return [];
    }
  };

  // Handle selecting an existing team from autocomplete
  const handleSelectExistingTeam = (teamData: TeamApiResponse) => {
    const normalizedTeam = normalizeTeams([teamData])[0];
    setEditingItem(normalizedTeam);
    setFormData(withTeamFormDefaults(normalizedTeam));
  };

  const handleManageRoster = async (team: Team) => {
    setSelectedTeam(team);
    clearRosterFieldErrors();
    setRosterFormError(null);
    setShowRosterModal(true);
    setRosterPage(1);
    const latestPlayers = await fetchAllPlayers();
    await fetchTeamRoster(
      team.team_id,
      "modal",
      1,
      rosterPageSize,
      latestPlayers,
    );
  };

  const handleAddPlayerToRoster = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeam) return;
    if (isAddingPlayerToRoster) return;

    try {
      setIsAddingPlayerToRoster(true);
      setRosterFormError(null);
      clearRosterFieldErrors();
      const selectedIsAvailable = availablePlayers.some(
        (player) => player.player_id === rosterFormData.player_id,
      );
      if (!selectedIsAvailable) {
        setRosterFieldError("player_id", t("validation.invalidSelection"));
        setRosterFormError(t("validation.fixErrors"));
        return;
      }
      const payload = {
        ...rosterFormData,
        team_id: selectedTeam.team_id,
      };
      await apiClient.post(`/teams/${selectedTeam.team_id}/players`, payload);
      const freshPlayers = await fetchAllPlayers();
      await fetchTeamRoster(
        selectedTeam.team_id,
        "refresh",
        undefined,
        undefined,
        freshPlayers,
      );
      setRosterFormData({
        player_id: "",
        jersey_number: 1,
        position: "PG",
        is_active: true,
      });
      setRosterPlayerSearch("");
      setIsPlayerPickerOpen(false);
      setIsRosterPositionPickerOpen(false);
      setRosterFormError(null);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const handled = applyBackendValidationErrors(detail, {
        setFieldError: setRosterFieldError,
        clearErrors: clearRosterFieldErrors,
        translate: t,
      });
      if (handled) {
        setRosterFormError(t("validation.fixErrors"));
        return;
      }
      if (typeof detail === "string") {
        if (handleRosterStringError(detail)) {
          return;
        }
        setRosterFormError(detail);
      } else {
        setRosterFormError(t("errorSavingData"));
      }
    } finally {
      setIsAddingPlayerToRoster(false);
    }
  };

  const handleRemovePlayerFromRoster = async (playerId: string) => {
    if (!selectedTeam) return;
    if (!window.confirm(t("confirmDelete"))) return;

    try {
      await apiClient.delete(
        `/teams/${selectedTeam.team_id}/players/${playerId}`,
      );
      await fetchTeamRoster(selectedTeam.team_id);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") {
        setRosterFormError(detail);
      } else {
        setRosterFormError(t("errorDeletingData"));
      }
    }
  };

  const handleCloseRosterModal = () => {
    setShowRosterModal(false);
    setSelectedTeam(null);
    setTeamRoster([]);
    setAvailablePlayers([]);
    setIsRosterModalLoading(false);
    setIsRosterPaginationLoading(false);
    setRosterPositionFilter("ALL");
    setIsRosterFilterOpen(false);
    setIsPlayerPickerOpen(false);
    setIsRosterPositionPickerOpen(false);
    setRosterPlayerSearch("");
    clearRosterFieldErrors();
    setRosterFormError(null);
  };

  const getPlayerName = (playerId: string): string => {
    const player = players.find((p) => p.player_id === playerId);
    return player?.name || playerId;
  };

  const getPositionBadgeColor = (position: PlayerPosition) => {
    // Guards
    if (["PG", "SG"].includes(position)) return "bg-blue-100 text-blue-800";
    // Forwards
    if (["SF", "PF"].includes(position)) return "bg-green-100 text-green-800";
    // Center
    if (position === "C") return "bg-purple-100 text-purple-800";
    return "bg-gray-100 text-gray-800";
  };

  const filteredTeams = teams.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.country_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGender = !genderFilter || item.gender === genderFilter;
    return matchesSearch && matchesGender;
  });

  const filteredRoster = (
    rosterPositionFilter === "ALL"
      ? teamRoster
      : teamRoster.filter((tp) => tp.position === rosterPositionFilter)
  )
    .slice()
    .sort(
      (a, b) => (a.jersey_number ?? Infinity) - (b.jersey_number ?? Infinity),
    );

  // Client-side pagination over the globally-sorted roster
  const clientRosterTotalPages = Math.max(
    1,
    Math.ceil(filteredRoster.length / rosterPageSize),
  );
  const paginatedRoster = filteredRoster.slice(
    (rosterPage - 1) * rosterPageSize,
    rosterPage * rosterPageSize,
  );

  const rosterPlayerCandidates = availablePlayers.filter((player) => {
    const query = rosterPlayerSearch.toLowerCase().trim();
    if (!query) return true;
    return (
      player.name.toLowerCase().includes(query) ||
      player.player_id.toLowerCase().includes(query) ||
      String(player.position || "")
        .toLowerCase()
        .includes(query)
    );
  });

  const selectedRosterPlayer = availablePlayers.find(
    (player) => player.player_id === rosterFormData.player_id,
  );

  if (loading && !hasInitialLoadCompleted) {
    return <LoadingSpinner size="lg" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <Shield className="h-8 w-8 text-indigo-600" />
          <h1 className="text-3xl font-bold text-gray-900">{t("teams")}</h1>
        </div>
        {user?.role === "admin" && (
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{t("createTeam")}</span>
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="card grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder={t("common:common.search")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <select
            value={genderFilter}
            onChange={(e) =>
              setGenderFilter(e.target.value as "male" | "female" | "")
            }
            className="input pl-10 w-full"
          >
            <option value="">
              {t("common:common.gender")}: {t("common:common.filter")}
            </option>
            <option value="male">{t("common:common.male")}</option>
            <option value="female">{t("common:common.female")}</option>
          </select>
        </div>
      </div>

      <div className="card">
        {filteredTeams.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {searchTerm || genderFilter ? t("noData") : t("noTeams")}
          </div>
        ) : (
          <>
            <div className="relative">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t("common:common.name")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t("localizedNames")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t("shortName")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t("common:common.country")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t("common:common.gender")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t("manager")}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t("common:common.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredTeams.map((item) => (
                      <tr key={item._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {item.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          <div className="space-y-1">
                            {ADMIN_LOCALES.map((locale) => (
                              <div
                                key={locale}
                                className="flex items-center text-xs"
                              >
                                <span className="font-semibold uppercase text-gray-500 mr-2">
                                  {locale}
                                </span>
                                <span>{item.i18n_names?.[locale] || "-"}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {item.short_name}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {item.country_name}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              item.gender === "male"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-pink-100 text-pink-800"
                            }`}
                          >
                            {t(item.gender)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {item.manager?.name || "-"}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleManageRoster(item)}
                            className="text-green-600 hover:text-green-900"
                            title={t("roster")}
                          >
                            <Users className="h-5 w-5 inline" />
                          </button>
                          {user?.role === "admin" && (
                            <>
                              <button
                                onClick={() => handleEdit(item)}
                                className="text-blue-600 hover:text-blue-900"
                                title={t("common:common.edit")}
                              >
                                <Edit className="h-5 w-5 inline" />
                              </button>
                              <button
                                onClick={() => handleDelete(item.team_id)}
                                className="text-red-600 hover:text-red-900"
                                title={t("common:common.delete")}
                              >
                                <Trash2 className="h-5 w-5 inline" />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={(page) => {
                  if (page === currentPage) return;
                  setIsTeamsPaginationLoading(true);
                  setCurrentPage(page);
                }}
                onPageSizeChange={(newPageSize) => {
                  if (newPageSize === pageSize && currentPage === 1) return;
                  setIsTeamsPaginationLoading(true);
                  setPageSize(newPageSize);
                  setCurrentPage(1);
                }}
              />
              {isTeamsPaginationLoading && (
                <div
                  className="absolute inset-0 bg-white/70 flex items-center justify-center z-10"
                  data-testid="teams-pagination-loading"
                >
                  <LoadingSpinner size="sm" />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Team Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-2xl font-bold">
                {editingItem ? t("common:common.edit") : t("createTeam")}
              </h2>
              <button
                onClick={handleCloseForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <form
              id="team-form"
              onSubmit={handleSubmit}
              className="p-6 space-y-4 max-h-[calc(90vh-8rem)] overflow-y-auto"
            >
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}
              {showChangeWarning &&
                changeResult &&
                changeResult.changes.length > 0 && (
                  <ChangeConfirmation
                    changePercentage={changeResult.change_percentage}
                    changes={changeResult.changes}
                    onConfirm={handleConfirmChanges}
                    onCancel={handleCancelChanges}
                  />
                )}
              {showDuplicateWarning &&
                duplicates &&
                duplicates.has_duplicates && (
                  <DuplicateWarning
                    duplicates={duplicates.duplicates}
                    onContinue={handleContinueWithDuplicates}
                    onCancel={handleCancelDuplicates}
                    entityType="team"
                    onViewSimilarRecords={
                      duplicates.duplicates.length > 0
                        ? () => {
                            setSimilarRecords(duplicates.duplicates);
                            setShowSimilarRecords(true);
                          }
                        : undefined
                    }
                  />
                )}
              <div>
                <AutocompleteSearch<TeamApiResponse>
                  name="name"
                  value={formData.name || ""}
                  onChange={(value) => {
                    setFormData({ ...formData, name: value });
                    validateAndSetFieldError("name", value);
                  }}
                  onSelectSuggestion={handleSelectExistingTeam}
                  fetchSuggestions={fetchTeamSuggestions}
                  getDisplayText={(team) => team.name}
                  getSecondaryText={(team) =>
                    `${team.short_name} • ${team.country_name} • ${team.gender}`
                  }
                  placeholder={t("teamNamePlaceholder")}
                  label={t("common:common.name")}
                  required
                  minChars={3}
                />
                {getFieldError("name") && (
                  <p className="mt-1 text-sm text-red-600">
                    {getFieldError("name")}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {t("localizedNames")}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t("localizedNamesDescription")}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {ADMIN_LOCALES.map((locale) => {
                    const fieldKey = `i18n_names.${locale}`;
                    return (
                      <div key={locale}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {locale === "en"
                            ? t("localizedNameEn")
                            : t("localizedNameEs")}
                        </label>
                        <input
                          type="text"
                          maxLength={100}
                          value={formData.i18n_names?.[locale] ?? ""}
                          onChange={(e) =>
                            handleLocalizedNameChange(locale, e.target.value)
                          }
                          onBlur={(e) =>
                            validateAndSetFieldError(fieldKey, e.target.value)
                          }
                          className={`input w-full ${
                            getFieldError(fieldKey) ? "border-red-500" : ""
                          }`}
                        />
                        {getFieldError(fieldKey) && (
                          <p className="mt-1 text-sm text-red-600">
                            {getFieldError(fieldKey)}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("shortName")} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.short_name}
                    onChange={(e) => {
                      setFormData({ ...formData, short_name: e.target.value });
                      validateAndSetFieldError("short_name", e.target.value);
                    }}
                    onBlur={(e) =>
                      validateAndSetFieldError("short_name", e.target.value)
                    }
                    className={`input w-full ${
                      getFieldError("short_name") ? "border-red-500" : ""
                    }`}
                  />
                  {getFieldError("short_name") && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError("short_name")}
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("common:common.country")} *
                  </label>
                  <select
                    required
                    value={formData.country_name}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        country_name: e.target.value,
                      });
                      validateAndSetFieldError("country_name", e.target.value);
                    }}
                    onBlur={(e) =>
                      validateAndSetFieldError("country_name", e.target.value)
                    }
                    className={`input w-full ${
                      getFieldError("country_name") ? "border-red-500" : ""
                    }`}
                  >
                    <option value="">{t("common:common.selectCountry")}</option>
                    {getCountriesSorted(currentLang).map((country) => (
                      <option key={country.en} value={country.en}>
                        {country[currentLang]}
                      </option>
                    ))}
                  </select>
                  {getFieldError("country_name") && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError("country_name")}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("common:common.gender")} *
                  </label>
                  <select
                    required
                    value={formData.gender}
                    onChange={(e) => {
                      const value = e.target.value as "male" | "female";
                      setFormData({ ...formData, gender: value });
                      validateAndSetFieldError("gender", value);
                    }}
                    onBlur={(e) =>
                      validateAndSetFieldError("gender", e.target.value)
                    }
                    className={`input w-full ${
                      getFieldError("gender") ? "border-red-500" : ""
                    }`}
                  >
                    <option value="male">{t("common:common.male")}</option>
                    <option value="female">{t("common:common.female")}</option>
                  </select>
                  {getFieldError("gender") && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError("gender")}
                    </p>
                  )}
                </div>
              </div>
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-3">{t("manager")}</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("managerName")} *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.manager?.name || ""}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          manager: {
                            ...getManagerDraft(),
                            name: e.target.value,
                          },
                        });
                        validateAndSetFieldError(
                          "manager.name",
                          e.target.value,
                        );
                      }}
                      onBlur={(e) =>
                        validateAndSetFieldError("manager.name", e.target.value)
                      }
                      className={`input w-full ${
                        getFieldError("manager.name") ? "border-red-500" : ""
                      }`}
                    />
                    {getFieldError("manager.name") && (
                      <p className="mt-1 text-sm text-red-600">
                        {getFieldError("manager.name")}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("managerNationality")}
                    </label>
                    <select
                      value={formData.manager?.country_name || ""}
                      onChange={(e) => {
                        const value = e.target.value || "";
                        setFormData({
                          ...formData,
                          manager: {
                            ...getManagerDraft(),
                            country_name: value,
                          },
                        });
                        validateAndSetFieldError(
                          "manager.country_name",
                          value || undefined,
                        );
                      }}
                      onBlur={(e) =>
                        validateAndSetFieldError(
                          "manager.country_name",
                          e.target.value || undefined,
                        )
                      }
                      className={`input w-full ${
                        getFieldError("manager.country_name")
                          ? "border-red-500"
                          : ""
                      }`}
                    >
                      <option value="">
                        {t("common:common.selectCountry")}
                      </option>
                      {getCountriesSorted(currentLang).map((country) => (
                        <option key={country.en} value={country.en}>
                          {country[currentLang]}
                        </option>
                      ))}
                    </select>
                    {getFieldError("manager.country_name") && (
                      <p className="mt-1 text-sm text-red-600">
                        {getFieldError("manager.country_name")}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Technical Staff Section */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold">
                    {t("technicalStaff")}
                  </h3>
                  <button
                    type="button"
                    onClick={handleAddStaff}
                    className="btn btn-sm btn-secondary flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" /> {t("addStaff")}
                  </button>
                </div>
                {formData.technical_staff &&
                  formData.technical_staff.length > 0 && (
                    <div className="space-y-4">
                      {formData.technical_staff.map((staff, index) => (
                        <div
                          key={index}
                          className="border rounded-lg p-4 bg-gray-50"
                        >
                          <div className="flex justify-between items-center mb-3">
                            <span className="font-medium text-sm text-gray-700">
                              {t("staffMember")} #{index + 1}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveStaff(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t("common:common.name")}
                                </label>
                                <input
                                  type="text"
                                  value={staff.name || ""}
                                  onChange={(e) =>
                                    handleUpdateStaff(
                                      index,
                                      "name",
                                      e.target.value,
                                    )
                                  }
                                  className="input w-full"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t("role")}
                                </label>
                                <select
                                  value={staff.role || ""}
                                  onChange={(e) =>
                                    handleUpdateStaff(
                                      index,
                                      "role",
                                      e.target.value,
                                    )
                                  }
                                  className="input w-full"
                                >
                                  <option value="">
                                    {t("rolePlaceholder")}
                                  </option>
                                  {TECHNICAL_STAFF_ROLES.map((role) => (
                                    <option key={role} value={role}>
                                      {t(`technicalStaffRoles.${role}`)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("common:common.country")}
                              </label>
                              <select
                                value={staff.country_name || ""}
                                onChange={(e) =>
                                  handleUpdateStaff(
                                    index,
                                    "country_name",
                                    e.target.value,
                                  )
                                }
                                className="input w-full"
                              >
                                <option value="">
                                  {t("common:common.selectCountry")}
                                </option>
                                {getCountriesSorted(currentLang).map(
                                  (country) => (
                                    <option key={country.en} value={country.en}>
                                      {country[currentLang]}
                                    </option>
                                  ),
                                )}
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>

              <div className="flex justify-end space-x-3 pt-4 sticky bottom-0 bg-white border-t border-gray-200 -mx-6 px-6 py-4">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="btn btn-secondary"
                >
                  {t("cancel")}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={showChangeWarning || showSimilarRecords}
                >
                  {t("save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Roster Management Modal */}
      {showRosterModal && selectedTeam && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          data-testid="roster-modal"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-2xl font-bold">
                {t("roster")} - {selectedTeam.name}
              </h2>
              <button
                onClick={handleCloseRosterModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Current Roster */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    {t("starters")} ({rosterTotalItems})
                  </h3>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("position")}
                    </label>
                    <div
                      className="relative"
                      data-testid="roster-filter-position"
                    >
                      <button
                        type="button"
                        data-testid="roster-filter-position-toggle"
                        onClick={() => setIsRosterFilterOpen((prev) => !prev)}
                        className="input w-full text-left flex items-center justify-between"
                      >
                        <span>
                          {rosterPositionFilter === "ALL"
                            ? t("allPositions")
                            : t(`positions.${rosterPositionFilter}`)}
                        </span>
                        <span className="text-gray-500">▾</span>
                      </button>
                      {isRosterFilterOpen && (
                        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
                          <button
                            type="button"
                            data-testid="roster-filter-position-option-ALL"
                            onClick={() => {
                              setRosterPositionFilter("ALL");
                              setIsRosterFilterOpen(false);
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-gray-50"
                          >
                            {t("allPositions")}
                          </button>
                          {ROSTER_POSITION_OPTIONS.map((position) => (
                            <button
                              key={`filter-${position}`}
                              type="button"
                              data-testid={`roster-filter-position-option-${position}`}
                              onClick={() => {
                                setRosterPositionFilter(position);
                                setIsRosterFilterOpen(false);
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-gray-50"
                            >
                              {t(`positions.${position}`)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {isRosterModalLoading ? (
                    <div
                      className="flex items-center justify-center py-12"
                      data-testid="roster-modal-loading"
                    >
                      <LoadingSpinner size="sm" />
                    </div>
                  ) : filteredRoster.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {rosterPositionFilter === "ALL"
                        ? t("noPlayers")
                        : t("noData")}
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="space-y-2">
                        {paginatedRoster.map((tp) => (
                          <div
                            key={tp.player_id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                <span className="font-semibold text-lg">
                                  #{tp.jersey_number}
                                </span>
                                <span className="font-medium">
                                  {getPlayerName(tp.player_id)}
                                </span>
                                <span
                                  className={`px-2 py-1 rounded text-xs font-medium ${getPositionBadgeColor(
                                    tp.position,
                                  )}`}
                                >
                                  {t(`positions.${tp.position}`)}
                                </span>
                                {!tp.is_active && (
                                  <span className="px-2 py-1 rounded text-xs font-medium bg-gray-300 text-gray-700">
                                    {t("inactive")}
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() =>
                                handleRemovePlayerFromRoster(tp.player_id)
                              }
                              className="text-red-600 hover:text-red-900"
                              title={t("removeFromRoster")}
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4">
                        <Pagination
                          currentPage={rosterPage}
                          totalPages={clientRosterTotalPages}
                          totalItems={filteredRoster.length}
                          pageSize={rosterPageSize}
                          onPageChange={(page) => {
                            if (page === rosterPage) return;
                            setRosterPage(page);
                          }}
                          onPageSizeChange={(newPageSize) => {
                            if (
                              newPageSize === rosterPageSize &&
                              rosterPage === 1
                            ) {
                              return;
                            }
                            setRosterPageSize(newPageSize);
                            setRosterPage(1);
                          }}
                        />
                      </div>
                      {isRosterPaginationLoading && (
                        <div
                          className="absolute inset-0 bg-white/70 flex items-center justify-center z-10"
                          data-testid="roster-pagination-loading"
                        >
                          <LoadingSpinner size="sm" />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Add Player Form */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    {t("addToRoster")}
                  </h3>
                  <form
                    onSubmit={handleAddPlayerToRoster}
                    className="space-y-4"
                  >
                    {rosterFormError && (
                      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                        {rosterFormError}
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("players")} *
                      </label>
                      <div
                        className="space-y-2"
                        data-testid="roster-player-picker"
                      >
                        <button
                          type="button"
                          data-testid="roster-player-picker-toggle"
                          onClick={() => setIsPlayerPickerOpen((prev) => !prev)}
                          className={`input w-full text-left flex items-center justify-between ${
                            rosterFieldErrors.player_id ? "border-red-500" : ""
                          }`}
                        >
                          <span>
                            {selectedRosterPlayer
                              ? `${selectedRosterPlayer.name} - ${t(
                                  `positions.${selectedRosterPlayer.position}`,
                                )}`
                              : t("selectPlayers")}
                          </span>
                          <span className="text-gray-500">▾</span>
                        </button>
                        {isPlayerPickerOpen && (
                          <div
                            className="border border-gray-200 rounded-md p-2 space-y-2"
                            data-testid="roster-player-picker-panel"
                          >
                            <input
                              type="text"
                              value={rosterPlayerSearch}
                              onChange={(e) =>
                                setRosterPlayerSearch(e.target.value)
                              }
                              placeholder={t("search")}
                              data-testid="roster-player-search"
                              className="input w-full"
                            />
                            <div className="max-h-52 overflow-auto border border-gray-100 rounded-md">
                              {rosterPlayerCandidates.length === 0 ? (
                                <div className="px-3 py-2 text-sm text-gray-500">
                                  {t("noData")}
                                </div>
                              ) : (
                                rosterPlayerCandidates.map((player) => (
                                  <button
                                    key={player.player_id}
                                    type="button"
                                    data-testid={`roster-available-player-option-${player.player_id}`}
                                    onClick={() => {
                                      clearRosterFieldError("player_id");
                                      setRosterFormData({
                                        ...rosterFormData,
                                        player_id: player.player_id,
                                      });
                                      setRosterPlayerSearch("");
                                      setIsPlayerPickerOpen(false);
                                    }}
                                    className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b last:border-b-0"
                                  >
                                    {player.name} -{" "}
                                    {t(`positions.${player.position}`)}
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      {rosterFieldErrors.player_id && (
                        <p className="mt-1 text-sm text-red-600">
                          {rosterFieldErrors.player_id}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("jerseyNumber")} *
                        </label>
                        <input
                          type="number"
                          required
                          min="1"
                          max="99"
                          value={rosterFormData.jersey_number}
                          onChange={(e) => {
                            clearRosterFieldError("jersey_number");
                            setRosterFormData({
                              ...rosterFormData,
                              jersey_number: parseInt(e.target.value),
                            });
                          }}
                          className={`input w-full ${
                            rosterFieldErrors.jersey_number
                              ? "border-red-500"
                              : ""
                          }`}
                        />
                        {rosterFieldErrors.jersey_number && (
                          <p className="mt-1 text-sm text-red-600">
                            {rosterFieldErrors.jersey_number}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("position")} *
                        </label>
                        <div
                          className="relative"
                          data-testid="roster-position-picker"
                        >
                          <button
                            type="button"
                            data-testid="roster-position-toggle"
                            onClick={() =>
                              setIsRosterPositionPickerOpen((prev) => !prev)
                            }
                            className={`input w-full text-left flex items-center justify-between ${
                              rosterFieldErrors.position ? "border-red-500" : ""
                            }`}
                          >
                            <span>
                              {t(`positions.${rosterFormData.position}`)}
                            </span>
                            <span className="text-gray-500">▾</span>
                          </button>
                          {isRosterPositionPickerOpen && (
                            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
                              {ROSTER_POSITION_OPTIONS.map((position) => (
                                <button
                                  key={`roster-position-${position}`}
                                  type="button"
                                  data-testid={`roster-position-option-${position}`}
                                  onClick={() => {
                                    clearRosterFieldError("position");
                                    setRosterFormData({
                                      ...rosterFormData,
                                      position,
                                    });
                                    setIsRosterPositionPickerOpen(false);
                                  }}
                                  className="w-full px-3 py-2 text-left hover:bg-gray-50"
                                >
                                  {t(`positions.${position}`)}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {rosterFieldErrors.position && (
                          <p className="mt-1 text-sm text-red-600">
                            {rosterFieldErrors.position}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={rosterFormData.is_active}
                        onChange={(e) =>
                          setRosterFormData({
                            ...rosterFormData,
                            is_active: e.target.checked,
                          })
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="is_active"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        {t("active")}
                      </label>
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary w-full"
                      disabled={isAddingPlayerToRoster}
                    >
                      {isAddingPlayerToRoster ? (
                        <span className="inline-block animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2" />
                      ) : (
                        <Plus className="h-5 w-5 inline mr-2" />
                      )}
                      {t("addToRoster")}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Similar Records Viewer Modal */}
      {showSimilarRecords && similarRecords && similarRecords.length > 0 && (
        <SimilarRecordsViewer
          records={similarRecords}
          currentData={formData}
          entityType="team"
          onClose={handleCloseSimilarRecords}
          onOpenRecord={handleOpenSimilarRecord}
        />
      )}
    </div>
  );
}
